import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, Calendar } from 'lucide-react'
import { useLanguageStore } from '../../lib/store'
import { Helmet } from 'react-helmet-async'

interface PageData {
    id: number
    slug: string
    title_en: string
    title_vi: string
    content_en: string
    content_vi: string
    meta_description_en: string
    meta_description_vi: string
    meta_keywords: string
    updated_at: string
}

export default function StaticPage() {
    const location = useLocation()
    // Extract slug from pathname (e.g., /about -> about, /faq -> faq)
    const slug = location.pathname.replace('/', '')

    useTranslation()
    const { language } = useLanguageStore()

    const [page, setPage] = useState<PageData | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        const fetchPage = async () => {
            if (!slug) return
            setLoading(true)
            setError(null)

            try {
                const response = await fetch(`/api/pages/${slug}`)
                const data = await response.json()

                if (data.success && data.data) {
                    setPage(data.data)
                } else {
                    setError(data.error || 'Page not found')
                }
            } catch (err) {
                console.error('Error fetching page:', err)
                setError('Failed to load page')
            } finally {
                setLoading(false)
            }
        }

        fetchPage()
    }, [slug])

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-pulse text-center">
                    <div className="w-16 h-16 bg-primary-200 rounded-full mx-auto mb-4"></div>
                    <p className="text-gray-500">{language === 'en' ? 'Loading...' : 'Đang tải...'}</p>
                </div>
            </div>
        )
    }

    if (error || !page) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
                <h1 className="text-2xl font-bold mb-4">{language === 'en' ? 'Page Not Found' : 'Không tìm thấy trang'}</h1>
                <Link to="/" className="text-primary-600 hover:underline">
                    {language === 'en' ? 'Back to Home' : 'Về trang chủ'}
                </Link>
            </div>
        )
    }

    const title = language === 'en' ? page.title_en : (page.title_vi || page.title_en)
    const content = language === 'en' ? page.content_en : (page.content_vi || page.content_en)
    const metaDescription = language === 'en' ? page.meta_description_en : (page.meta_description_vi || page.meta_description_en)

    return (
        <>
            <Helmet>
                <title>{title} | Phú Quốc Rental</title>
                <meta name="description" content={metaDescription || ''} />
                {page.meta_keywords && <meta name="keywords" content={page.meta_keywords} />}
                <link rel="canonical" href={`https://phuquoc-rental.pages.dev/${slug}`} />
            </Helmet>

            <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-8">
                <div className="container mx-auto px-4">
                    {/* Breadcrumb */}
                    <div className="mb-6">
                        <Link
                            to="/"
                            className="inline-flex items-center text-gray-600 hover:text-primary-600 transition"
                        >
                            <ChevronLeft className="w-4 h-4 mr-1" />
                            {language === 'en' ? 'Back to Home' : 'Về trang chủ'}
                        </Link>
                    </div>

                    {/* Page Content */}
                    <article className="max-w-4xl mx-auto">
                        <header className="mb-8 text-center">
                            <h1 className="text-3xl md:text-4xl font-bold mb-4">{title}</h1>
                            {page.updated_at && (
                                <p className="text-sm text-gray-500 flex items-center justify-center gap-2">
                                    <Calendar className="w-4 h-4" />
                                    {language === 'en' ? 'Last updated' : 'Cập nhật'}: {page.updated_at.split('T')[0]}
                                </p>
                            )}
                        </header>

                        <div className="bg-white rounded-2xl shadow-sm border p-6 md:p-10">
                            <div
                                className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-a:text-primary-600 prose-ul:text-gray-700 prose-li:marker:text-primary-500"
                                dangerouslySetInnerHTML={{ __html: content || '' }}
                            />
                        </div>
                    </article>
                </div>
            </div>
        </>
    )
}
